import React, { useState, useEffect } from 'react';
import { usePatchJobMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { isJobLocked } from '../../utils/lockHelpers';
import { formatDateForInput, validateJobDateField } from '../../utils/jobDateValidation';
import type { JobDatesData, JobFormData } from '../../utils/types';

export type InlineProductionDateField = 'glass_arrival_date' | 'manufacture_date';

const DATE_INPUT_CLASS =
  'form-input text-sm w-full min-w-[140px] border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

interface InlineJobProductionDateEditorProps {
  job: {
    id: string;
    website_job_number?: string | null;
    glass_arrival_date?: string | null;
    manufacture_date?: string | null;
    potential_delivery_date?: string | null;
    lock_data?: unknown;
  };
  field: InlineProductionDateField;
  onUpdated: () => void;
}

/**
 * Inline date editor for glass arrival / manufacture on Manage Routes.
 * Uses the same validation rules as JobDatesManager (validateJobDateField + patchJob).
 */
export const InlineJobProductionDateEditor: React.FC<InlineJobProductionDateEditorProps> = ({
  job,
  field,
  onUpdated,
}) => {
  const [patchJob, { isLoading }] = usePatchJobMutation();
  const toast = useToast();
  const locked = isJobLocked(job.lock_data);

  const displayValue = field === 'glass_arrival_date' ? job.glass_arrival_date : job.manufacture_date;
  const savedInputValue = formatDateForInput(displayValue);
  const [localValue, setLocalValue] = useState(savedInputValue);

  useEffect(() => {
    setLocalValue(savedInputValue);
  }, [savedInputValue, job.id, field]);

  const allDatesBase = {
    glass_arrival_date: job.glass_arrival_date || '',
    manufacture_date: job.manufacture_date || '',
    potential_delivery_date: job.potential_delivery_date || '',
  };

  const handleChange = async (newValue: string) => {
    if (newValue === savedInputValue) {
      return;
    }
    if (locked) {
      toast.warning('Locked', 'This job is locked and dates cannot be changed');
      setLocalValue(savedInputValue);
      return;
    }

    const proposedDates = {
      ...allDatesBase,
      [field]: newValue || '',
    };

    const err = validateJobDateField(field, newValue, {
      allDates: proposedDates,
      hideGlassArrival: false,
    });
    if (err) {
      toast.error('Date Validation Error', err);
      setLocalValue(savedInputValue);
      return;
    }

    try {
      const data: Partial<JobDatesData> = {
        [field]: newValue || null,
      };
      await patchJob({ id: job.id, data: data as Partial<JobFormData> }).unwrap();
      toast.success(`Job #${job.website_job_number || job.id} updated`);
      onUpdated();
    } catch (e: unknown) {
      setLocalValue(savedInputValue);
      const message =
        e && typeof e === 'object' && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error(message || 'Failed to update date');
    }
  };

  const minForManufacture =
    field === 'manufacture_date'
      ? formatDateForInput(job.glass_arrival_date) || undefined
      : undefined;

  return (
    <input
      type="date"
      value={localValue}
      min={minForManufacture}
      onChange={(e) => {
        setLocalValue(e.target.value);
        void handleChange(e.target.value);
      }}
      disabled={locked || isLoading}
      title={locked ? 'Job is locked' : undefined}
      className={DATE_INPUT_CLASS}
    />
  );
};
